import { totalResources, type Resources } from './resources';
import { houseRules, victoryPoints } from './rules';
import type { DevCard, GameEvent, GameState, PlayerState } from './types';

export interface PlayerView extends Omit<PlayerState, 'resources' | 'devCards'> {
  /** Null for other players' hands. */
  resources: Resources | null;
  resourceCount: number;
  /** Null for other players' cards. */
  devCards: DevCard[] | null;
  /** Cards in hand that others can't identify (VP cards are excluded when they're revealed). */
  devCardCount: number;
  /** VP cards everyone can see; 0 unless the reveal house rule is on (or it's your own view). */
  victoryPointCards: number;
  /** Points this viewer can see: hidden VP cards are left out of other players' scores. */
  victoryPoints: number;
}

/** What one seat is allowed to see. `viewer` null is a spectator. */
export interface GameView extends Omit<GameState, 'rng' | 'devDeck' | 'players'> {
  viewer: number | null;
  devDeckCount: number;
  players: PlayerView[];
}

export function viewFor(state: GameState, viewer: number | null): GameView {
  const revealVp = houseRules(state).revealVictoryPoints;
  const { rng: _rng, devDeck, players, ...shared } = structuredClone(state);
  return {
    ...shared,
    viewer,
    devDeckCount: devDeck.length,
    players: players.map((p, i): PlayerView => {
      const mine = i === viewer;
      const vpCards = p.devCards.filter((c) => c.type === 'victoryPoint').length;
      // A finished game can be continued, so hands stay private; only the VP cards that explain the score are shown.
      const reveal = mine;
      const publicVp = revealVp || reveal || state.phase.kind === 'gameOver';
      return {
        ...p,
        resources: reveal ? p.resources : null,
        resourceCount: totalResources(p.resources),
        devCards: reveal ? p.devCards : null,
        devCardCount: p.devCards.length - (publicVp ? vpCards : 0),
        victoryPointCards: publicVp ? vpCards : 0,
        victoryPoints: victoryPoints(state, i) - (publicVp ? 0 : vpCards),
      };
    }),
  };
}

/** Redacts what `viewer` mustn't see. Pass the game's state so a VP card stays hidden when the reveal rule is off. */
export function eventsFor(events: readonly GameEvent[], viewer: number | null, state?: GameState): GameEvent[] {
  const revealVp = state ? houseRules(state).revealVictoryPoints : true;
  return events.map((e) => {
    if (e.type === 'stole' && viewer !== e.thief && viewer !== e.victim) return { ...e, resource: null };
    if (e.type === 'boughtDevCard' && viewer !== e.player && !(revealVp && e.card === 'victoryPoint')) {
      return { ...e, card: null };
    }
    return e;
  });
}
